import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, FileText, ChevronRight, X, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ukDate } from "../lib/format";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import { templateErrorMessage, templateConflict } from "../lib/jobTemplates";
import {
  PageHeader, Card, PrimaryButton, QuietButton, Field, Input,
  EmptyState
} from "../components/UI";

/**
 * Lightweight "create template" modal — name + job type, then redirects to
 * the full editor at /templates/:id. `initialJobType` pre-selects the job
 * type when arriving from Settings → Job Types ("Create default template").
 */
function CreateTemplateModal({ open, onClose, onCreated, initialJobType }) {
  const [name, setName] = useState("");
  const [jobTypeKey, setJobTypeKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { activeTypes, labelFor, loading: jtLoading } = useJobTypeLibrary();

  useEffect(() => {
    if (open) {
      setJobTypeKey(initialJobType || "");
      setName(initialJobType ? labelFor(initialJobType) : "");
      setError(null);
    }
  }, [open, initialJobType, labelFor]);

  if (!open) return null;

  const post = (replace) => api.post(`/templates${replace ? "?replace=true" : ""}`, {
    name: name.trim(), job_type: jobTypeKey || null, phases: [],
  });

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError("Name is required"); return; }
    setSaving(true);
    try {
      let res;
      try {
        res = await post(false);
      } catch (err) {
        const c = templateConflict(err);
        if (!c) throw err;
        if (!window.confirm(`"${c.name}" is already the template for ${labelFor(c.job_type)}. Replace it with this one? The old template is archived, not deleted.`)) {
          setSaving(false); return;
        }
        res = await post(true);
      }
      toast.success("Template created — add phases and lines on the next page");
      onCreated?.(res.data);
    } catch (err) {
      setError(templateErrorMessage(err, "Create failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-start justify-center pt-16 px-4">
      <div
        className="w-full max-w-[520px] bg-surface border border-line rounded-lg shadow-card overflow-hidden"
        data-testid="create-template-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h2 className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>
            New job template
          </h2>
          <button onClick={onClose} className="text-ink-3 hover:text-ink"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-5">
          <p className="text-ink-3 mb-4" style={{ fontSize: 12.5 }}>
            Give it a name to begin. You'll add phases, sub-phases and default library lines on the next screen.
          </p>
          <div className="grid grid-cols-1 gap-4">
            <Field label="Name" required>
              <Input
                value={name} onChange={setName}
                placeholder="e.g. Single Storey Extension"
                data-testid="new-template-name"
              />
            </Field>
            <Field label="Job type" hint="One live template per job type. New tenders of this type start from it automatically.">
              <select
                value={jobTypeKey}
                onChange={(e) => setJobTypeKey(e.target.value)}
                disabled={jtLoading}
                data-testid="new-template-job-type"
                className="w-full h-9 px-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                style={{ fontSize: 13 }}
              >
                <option value="">{jtLoading ? "Loading…" : "— No job type —"}</option>
                {activeTypes.map((jt) => (
                  <option key={jt.key} value={jt.key}>{jt.label}</option>
                ))}
              </select>
            </Field>
          </div>
          {error && (
            <div className="mt-4 px-3 py-2 rounded-md" style={{ background: "#FEF3E2", color: "#7A4C0A", fontSize: 12 }} data-testid="new-template-error">
              {error}
            </div>
          )}
          <div className="mt-5 flex items-center justify-end gap-2">
            <QuietButton onClick={onClose}>Cancel</QuietButton>
            <PrimaryButton
              type="submit" disabled={saving}
              icon={saving ? Loader2 : Save}
              data-testid="new-template-submit"
            >
              Create & continue
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Templates() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(params.get("new") === "1");
  const { labelFor: jobTypeLabelFor } = useJobTypeLibrary();
  const initialJobType = params.get("job_type") || "";

  const load = () => {
    setLoading(true);
    api.get("/templates")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const closeModal = () => {
    setOpen(false);
    if (params.get("new")) setParams({}, { replace: true });
  };
  const onCreated = (t) => {
    setOpen(false);
    navigate(`/templates/${t.id}`);
  };

  const subPhaseCount = (t) =>
    (t.phases || []).reduce((s, p) => s + (p.sub_phases || []).length, 0);
  const lineCount = (t) =>
    (t.phases || []).reduce((s, p) => s + (p.lines || []).length, 0);

  return (
    <div className="app-container py-8" data-testid="job-templates-page">
      <PageHeader
        title="Job Templates"
        subtitle="One template per job type: the phases, sub-phases and default library lines a new tender of that type starts with. Lines are priced from the live library the moment a tender is created."
        actions={
          <PrimaryButton icon={Plus} onClick={() => setOpen(true)} data-testid="add-template-button">
            Add template
          </PrimaryButton>
        }
      />

      <Card>
        {loading ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No job templates yet"
            hint="A job template pre-loads a new tender with its phases, sub-phases and default library lines."
            action={
              <PrimaryButton icon={Plus} onClick={() => setOpen(true)} data-testid="empty-add-template">
                Add template
              </PrimaryButton>
            }
          />
        ) : (
          <table className="w-full text-left" data-testid="templates-table">
            <thead>
              <tr className="border-b border-line bg-surface-2/50">
                <th className="col-head px-4 py-2.5">Name</th>
                <th className="col-head px-4 py-2.5">Job type</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 90 }}>Phases</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 110 }}>Sub-phases</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 90 }}>Lines</th>
                <th className="col-head px-4 py-2.5" style={{ width: 110 }}>Updated</th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((t, i) => (
                <tr
                  key={t.id}
                  onClick={() => navigate(`/templates/${t.id}`)}
                  data-testid={`template-row-${t.id}`}
                  className={[
                    "group cursor-pointer hover:bg-surface-2/60",
                    i !== items.length - 1 ? "border-b border-line-soft" : ""
                  ].join(" ")}
                >
                  <td className="px-4 py-3">
                    <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>{t.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    {t.job_type ? (
                      <span className="text-ink-2" style={{ fontSize: 12.5 }} data-testid={`template-job-type-${t.id}`}>
                        {jobTypeLabelFor(t.job_type)}
                        <span className="ml-1.5 rounded-full border border-line-soft px-1.5 py-0.5 text-ink-3" style={{ fontSize: 9.5, letterSpacing: "0.04em", textTransform: "uppercase" }}>default</span>
                      </span>
                    ) : <span className="text-ink-4" style={{ fontSize: 12.5 }}>—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-mono tnum text-ink" style={{ fontSize: 12.5 }}>
                      {(t.phases || []).length}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>
                      {subPhaseCount(t)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-mono tnum text-ink-2" style={{ fontSize: 12.5 }} data-testid={`template-line-count-${t.id}`}>
                      {lineCount(t)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono tnum text-ink-3" style={{ fontSize: 12 }}>
                      {ukDate(t.updated_at)}
                    </span>
                  </td>
                  <td className="pr-4 py-3 text-right">
                    <ChevronRight size={14} className="text-ink-4 group-hover:text-ink-2" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <CreateTemplateModal
        open={open}
        initialJobType={initialJobType}
        onClose={closeModal}
        onCreated={onCreated}
      />
    </div>
  );
}
